import { ResizeObserver } from "@juggle/resize-observer";
import { ResizeObserverCallback } from "@juggle/resize-observer/lib/ResizeObserverCallback";
import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { throttle } from "throttle-debounce";
import { selectPictureById } from "../../selectors/pictures.js";
import { getActiveSite } from "../../selectors/sites.js";
import { getActionModules } from "../../slices/modules/thunks.js";
import { selectTopLevelMainMenuItems } from "../../slices/pages.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import { createAppSelector } from "../../store/utils.js";
import headerBaseStyles from "../../stylesheets/components/header-base.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import pagePreviewStyles from "../../stylesheets/components/page-preview.module.css";
import {
  BaseModuleProps,
  CommonHeaderLayoutProps,
  HeaderModuleSettings,
  Language,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  buildBreadcrumb,
  checkIsHomePage,
  checkIsOnePager,
  getActionLinks,
  getActiveColorScheme,
  getActiveLogoId,
  getActivePagePath,
  getFallbackLanguage,
  getInlineSize,
  getMainPageURL,
  getNearestHeaderImagesModule,
  getWideMenuBreakpoint,
  layoutsWithLogoBackground,
} from "../../utils/utils.js";
import Breadcrumb from "../Breadcrumb.js";
import CompactHeaderBar from "../CompactHeaderBar.js";
import FixedHeader from "../FixedHeader.js";
import HeaderLayout1 from "../HeaderLayout1.js";
import HeaderLayout2 from "../HeaderLayout2.js";
import HeaderLayout3 from "../HeaderLayout3.js";
import HeaderLayout4 from "../HeaderLayout4.js";
import HeaderLayout5 from "../HeaderLayout5.js";

// default "markAsUsed" does not work if there are multiple css modules imported
void pagePreviewStyles["Page__Preview"];
void pagePreviewStyles["Page__Preview--dragging"];

type Props = BaseModuleProps<HeaderModuleSettings>;

const HeaderModule: FunctionComponent<Props> = ({
  isPreview,
  pageId,
  queries,
  translatedModule: {
    siteId,
    settings: {
      showBreadcrumb,
      sticky,
      menuVariant,
      showSouthTyrolLogo,
      layoutVariant,
      topHeaderVariant,
      logoSize,
      logoBackground,
      imageOverlayGradient: imageOverlayGradientFromProps,
      showMenuSeparators,
      whatsAppNumber,
      useFaviconAsLogo,
      logoId,
    },
    translation: { languageId },
  },
  translatedModule,
  activeModuleId,
}) => {
  const dispatch = useAppDispatch();

  const mainPageURL = useAppSelector((state) =>
    getMainPageURL(state.pages, languageId, isPreview),
  );
  const accommodation = useAppSelector(
    (state) => state.accommodation[languageId],
  );
  const site = useAppSelector((state) => getActiveSite(state.sites));
  const pages = useAppSelector((state) => state.pages);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const fallbackLanguageId = getFallbackLanguage(site, languageId);
  const isOnePager = checkIsOnePager(site);
  const isHomePage = checkIsHomePage(pages, pageId);
  const breadcrumb = buildBreadcrumb(
    pages,
    site,
    pageId,
    languageId,
    isPreview,
  );
  const imagesModule = useAppSelector((state) =>
    selectImagesModule(state, pageId, languageId),
  );
  const menuItems = useAppSelector((state) =>
    selectTopLevelMainMenuItems(
      state,
      languageId,
      isPreview,
      fallbackLanguageId,
    ),
  );
  const actionLinks = useAppSelector((state) =>
    selectActionLinks(state, languageId, isPreview),
  );
  const wideMenuBreakpoint = getWideMenuBreakpoint({
    actionLinks,
    topHeaderVariant,
    layoutVariant,
    menuItems,
    hasHeaderImage: !!imagesModule,
  });
  const activePagePath = useAppSelector(
    (state) => getActivePagePath(pageId, state.pages.byParentId),
    shallowEqual,
  );
  const logo = useAppSelector((state) =>
    selectPictureById(
      state,
      getActiveLogoId(state.mediaLibrary.logoIds, logoId),
    ),
  );
  const favicon =
    site.favicon.uploadId && site.favicon.uploadURL
      ? {
          id: site.favicon.uploadId,
          url: site.favicon.uploadURL,
          width: site.favicon.size ?? 32,
          height: site.favicon.size ?? 32,
          category: "logo" as const,
          title: site.accommodation.name,
          copyright: "",
        }
      : undefined;

  const [wideIntersectionEl, setWideIntersectionEl] =
    useState<HTMLElement | null>(null);

  const wideMenuFits = useWideMenuFits(wideMenuBreakpoint, isPreview);

  useEffect(() => {
    dispatch(getActionModules(siteId));
  }, []);

  const showCompactHeader =
    (!imagesModule || !imagesModule.pageId) &&
    layoutVariant !== "all-in-header" &&
    layoutVariant !== "fixed-sidebar";

  const showFixedHeader = layoutVariant !== "fixed-sidebar";
  const imageOverlayGradient =
    layoutsWithLogoBackground.includes(layoutVariant) &&
    logoBackground === "transparent"
      ? imageOverlayGradientFromProps
      : "dark";
  const isLayout3 = layoutVariant === "full-overlay-nav";
  const fixedHeaderLogo = favicon && useFaviconAsLogo ? favicon : logo;

  if (!accommodation) return null;

  const layoutProps: CommonHeaderLayoutProps = {
    accommodation,
    actionLinks,
    activeModuleId,
    activePagePath,
    fallbackLanguageId,
    imageOverlayGradient,
    imagesModule,
    isHomePage,
    isPreview,
    layoutVariant,
    languageId,
    logo,
    favicon,
    logoSize,
    logoBackground,
    useFaviconAsLogo,
    mainPageURL,
    menuItems,
    pageId,
    pages,
    queries,
    scheme,
    showSouthTyrolLogo,
    sticky,
    whatsAppNumber,
  };

  return (
    <header
      className={ClassNames(
        moduleBaseStyles.Module,
        css({ background: scheme.main.background, color: scheme.main.text }),
      )}
    >
      {showFixedHeader && (
        <FixedHeader
          actionLinks={actionLinks}
          activePagePath={activePagePath}
          hasHeaderImage={!!imagesModule}
          isPreview={isPreview}
          languageId={languageId}
          pages={pages}
          topLevelMainMenuItems={menuItems}
          scheme={scheme}
          stickyEnabled={sticky}
          fallbackLanguageId={fallbackLanguageId}
          pageId={pageId}
          mainPageURL={mainPageURL}
          logo={fixedHeaderLogo}
          logoSize={logoSize}
          intersectionTriggerEl={wideMenuFits ? wideIntersectionEl : null}
          accommodation={accommodation}
          isCircleHamburger={isLayout3}
          className={headerBaseStyles.HeaderModule__FixedHeader}
        />
      )}

      {showCompactHeader && (
        <div
          ref={setWideIntersectionEl}
          className={ClassNames(
            headerBaseStyles.HeaderModule__Inner,
            headerBaseStyles["HeaderModule__Inner--wide"],
            {
              [headerBaseStyles["HeaderModule__Inner--preview"]]: isPreview,
              [headerBaseStyles["HeaderModule__Inner--is-home"]]: isHomePage,
            },
          )}
        >
          <CompactHeaderBar
            actionLinks={actionLinks}
            activePagePath={activePagePath}
            hasHeaderImage={false}
            isPreview={isPreview}
            languageId={languageId}
            pages={pages}
            topLevelMainMenuItems={menuItems}
            scheme={scheme}
            fallbackLanguageId={fallbackLanguageId}
            pageId={pageId}
            mainPageURL={mainPageURL}
            logo={logo}
            logoSize={logoSize}
            accommodation={accommodation}
            isCircleHamburger={isLayout3}
          />
        </div>
      )}

      {layoutVariant === "all-in-header" && (
        <HeaderLayout1
          intersectionTriggerRef={setWideIntersectionEl}
          topHeaderVariant={topHeaderVariant}
          common={layoutProps}
          logoSize={logoSize}
        />
      )}

      {layoutVariant === "split-menu" && (
        <HeaderLayout2
          intersectionTriggerRef={setWideIntersectionEl}
          variant={menuVariant}
          common={layoutProps}
        />
      )}

      {isLayout3 && (
        <HeaderLayout3
          intersectionTriggerRef={setWideIntersectionEl}
          common={layoutProps}
        />
      )}

      {layoutVariant === "left-overlay-nav" && (
        <HeaderLayout4
          intersectionTriggerRef={setWideIntersectionEl}
          showMenuSeparators={showMenuSeparators}
          common={layoutProps}
        />
      )}

      {layoutVariant === "fixed-sidebar" && <HeaderLayout5 {...layoutProps} />}

      {!isOnePager && showBreadcrumb && (
        <Breadcrumb
          links={breadcrumb}
          isPreview={isPreview}
          languageId={languageId}
        />
      )}
    </header>
  );
};

const selectImagesModule = createAppSelector(
  [
    (state) => state.pages,
    (state) => state.modules,
    (_, pageId: string) => pageId,
    (_, _pageId: string, languageId: Language) => languageId,
  ],
  (pages, modules, pageId, languageId) =>
    getNearestHeaderImagesModule({
      pages,
      modules,
      currentPageId: pageId,
      languageId,
    }),
);

const selectActionLinks = createAppSelector(
  [
    (state) => state.modules,
    (state) => state.pages,
    (state) => state.sites,
    (_, languageId: Language) => languageId,
    (_, _languageId: Language, isPreview: boolean) => isPreview,
  ],
  (modules, pages, sites, languageId, isPreview) =>
    getActionLinks({
      modules,
      pages,
      site: getActiveSite(sites),
      languageId,
      isPreview,
    }),
);

// Check if the wide/compact menu fits
// and set classNames to <body> accordingly.
const useWideMenuFits = (
  wideMenuBreakpoint: number,
  isPreview: boolean,
): boolean => {
  const observer = useRef<ResizeObserver>(undefined);

  const wideMenuFitsClassName =
    headerBaseStyles["HeaderModule--wide-menu-fits"];

  const [wideMenuFits, setWideMenuFits] = useState(
    typeof document !== "undefined"
      ? document.body.classList.contains(wideMenuFitsClassName)
      : false,
  );

  useEffect(
    () => () => {
      observer.current?.disconnect();
    },
    [],
  );

  useEffect(() => {
    // The body class is modified here to avoid
    // FOUC on the published site (the generator adds this class to <body>)
    document.body.classList.toggle(wideMenuFitsClassName, wideMenuFits);
  }, [wideMenuFits]);

  useEffect(() => {
    observer.current?.disconnect();

    const onResize: ResizeObserverCallback = (entries) => {
      // Needs to be `window.innerWidth` on generated sites to match
      // the behaviour of `window.matchMedia`
      const width = isPreview ? getInlineSize(entries) : window.innerWidth;
      if (width === undefined) return;
      setWideMenuFits(width >= wideMenuBreakpoint);
    };

    const onResizeThrottled = throttle(200, onResize);
    observer.current = new ResizeObserver(onResizeThrottled);
    // TODO: better get the element the “React way” with refs
    const element =
      document.querySelector(`.${pagePreviewStyles.Page__Preview__Inner}`) ??
      document.body;
    element && observer.current.observe(element);
  }, [wideMenuBreakpoint]);

  return wideMenuFits;
};

export default HeaderModule;
